import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

export default function AdminLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { adminLogin } = useAuth()

  useEffect(() => {
    const savedCredentials = localStorage.getItem('adminLoginCredentials')
    if (savedCredentials) {
      try {
        const { email: savedEmail, password: savedPassword, remember } = JSON.parse(savedCredentials)
        if (remember) {
          setEmail(savedEmail || '')
          setPassword(savedPassword || '')
          setRememberMe(true)
        }
      } catch (error) {
        console.error('Error loading saved credentials:', error)
        localStorage.removeItem('adminLoginCredentials')
      }
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await adminLogin(email, password)

      if (result.success) {
        if (rememberMe) {
          localStorage.setItem('adminLoginCredentials', JSON.stringify({ email, password, remember: true }))
        } else {
          localStorage.removeItem('adminLoginCredentials')
        }
        window.location.href = '/dashboard'
      } else {
        setError(result.error || 'Admin login failed')
      }
    } catch (err) {
      setError('Login failed. Please check your credentials and try again.')
      console.error('Admin login error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo & Title */}
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">C</span>
            </div>
            <span className="text-2xl font-bold text-slate-900">CognaAssist</span>
          </div>
          <p className="text-sm text-slate-500">Admin Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 px-8 py-10">
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-slate-900 text-center">Welcome back</h3>
            <p className="text-sm text-slate-500 text-center mt-2">
              Sign in to access the admin dashboard
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-300 text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm placeholder:text-slate-400 transition-colors"
                placeholder="admin@cognavia.ai"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-300 text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm placeholder:text-slate-400 transition-colors"
                placeholder="Enter your password"
                disabled={loading}
              />
            </div>

            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-600">
                Remember my credentials
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2.5 px-4 text-sm font-medium rounded-xl text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white mr-2" />
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="text-center space-y-3">
              <p className="text-xs text-slate-400">Customer Access</p>
              <div className="flex items-center justify-center space-x-4">
                <Link to="/signin" className="text-brand-600 hover:text-brand-700 text-sm font-medium transition-colors">
                  Customer Login
                </Link>
                <span className="text-slate-300">|</span>
                <Link to="/register" className="text-brand-600 hover:text-brand-700 text-sm font-medium transition-colors">
                  Create Account
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs text-slate-400">
            {new Date().getFullYear()} Cognavia.ai. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
